import { notifyAndLogError, notifyAndWarn } from 'src/UI/common/ui-helpers';
import { EntityFields as UIEntityFields } from 'src/UI/dynamicUI/types';
import { fieldsToHide } from 'src/data-capture/merge-review/ReviewHelperFunctions';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import {
  UpsertEntityInput,
  useUpsertEntity,
} from 'src/data-capture/merge-review/useUpsertEntities';
import { useQueryClient } from '@tanstack/react-query';
import {
  DetectionState,
  EntityCategory,
  getDetectionsByCategorySelector,
  getPayloadForNewDetectionSelector,
  getPayloadForUpdateDetectionSelector,
  selectDetectionPayloadsForCategory,
  selectDetectionPayloadsForEntityType,
  selectDetectionSelector,
  selectExistingFields,
  selectExistingOwners,
  selectLastAction,
  useDetectedEntitiesStore,
} from 'src/data-capture/stores/useDetectedEntitiesStore';
import { Detection, EntityFields, FieldValue } from 'src/data-capture/types';
import { Logger } from 'src/utils/Logger';
import { useInvalidateDetections } from 'src/data-capture/hooks/file-uploads/useDetections';
import { useFlags } from 'src/feature-flags/FlagsHook';

export const entityFieldsToFilterOut = {
  entityType: 'PersonalDetails',
  fieldNames: ['first_name', 'last_name'],
};

export function useUpsertCategoryDetections(category: EntityCategory) {
  const setCategoryResolved = useDetectedEntitiesStore(state => state.setCategoryResolved);
  const setDetectionResolved = useDetectedEntitiesStore(state => state.setResolved);
  const setCategoryInsertLastActions = useDetectedEntitiesStore(
    state => state.setCategoryInsertLastActions,
  );
  const setCategoryActioning = useDetectedEntitiesStore(state => state.setCategoryActioning);
  const { upsertEntities } = useUpsertEntity();

  const upsertCategoryDetections = async (clientId: string) => {
    setCategoryActioning(true, category);
    const payloadsByDetection = selectDetectionPayloadsForCategory(
      useDetectedEntitiesStore.getState(),
      category,
    );
    const detectionsInOrder = Object.keys(payloadsByDetection).sort();
    const payloads = detectionsInOrder
      .map(detectionId => payloadsByDetection[detectionId])
      .filter((p): p is UpsertEntityInput => p !== null);
    try {
      const { data: ids } = await upsertEntities(
        clientId,
        payloads,
        false,
        entityFieldsToFilterOut,
        false,
      );
      const idsByDetectionId = detectionsInOrder.reduce((acc, detectionId, index) => {
        return { ...acc, [detectionId]: ids[index] };
      }, {});
      setCategoryResolved(true, category);
      setCategoryInsertLastActions(category, idsByDetectionId);
    } catch (e) {
      detectionsInOrder.forEach(detectionId => setDetectionResolved(false, category, detectionId));
      throw e;
    } finally {
      setCategoryActioning(false, category);
    }
  };

  return {
    upsertCategoryDetections,
  };
}

export function useUpsertEntityTypeDetections(category: EntityCategory, entityType: string) {
  const setEntitiesOfTypeResolved = useDetectedEntitiesStore(
    state => state.setEntitiesOfTypeResolved,
  );
  const setDetectionResolved = useDetectedEntitiesStore(state => state.setResolved);
  const setEntityOfTypeInsertLastAction = useDetectedEntitiesStore(
    state => state.setEntityOfTypeInsertLastAction,
  );
  const setEntitiesOfTypeActioning = useDetectedEntitiesStore(
    state => state.setEntitiesOfTypeActioning,
  );
  const { upsertEntities } = useUpsertEntity();

  const upsertEntityTypeDetections = async (clientId: string) => {
    setEntitiesOfTypeActioning(true, category, entityType);
    const payloadsByDetection = selectDetectionPayloadsForEntityType(
      useDetectedEntitiesStore.getState(),
      category,
      entityType,
    );
    const detectionsInOrder = Object.keys(payloadsByDetection).sort();

    const payloads = detectionsInOrder
      .map(detectionId => payloadsByDetection[detectionId])
      .filter((p): p is UpsertEntityInput => p !== null);

    try {
      const { data: ids } = await upsertEntities(
        clientId,
        payloads,
        false,
        entityFieldsToFilterOut,
        false,
      );

      const idsByDetectionId = detectionsInOrder.reduce((acc, detectionId, index) => {
        return { ...acc, [detectionId]: ids[index] };
      }, {});

      setEntityOfTypeInsertLastAction(category, entityType, idsByDetectionId);
      setEntitiesOfTypeResolved(true, category, entityType);
    } catch (e) {
      detectionsInOrder.forEach(detectionId => setDetectionResolved(false, category, detectionId));
      throw e;
    } finally {
      setEntitiesOfTypeActioning(false, category, entityType);
    }
  };

  return {
    upsertEntityTypeDetections,
  };
}

export default function useUpsertDetectedEntity(
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
) {
  const { upsertEntity } = useUpsertEntity();
  const lastAction = useDetectedEntitiesStore(state =>
    selectLastAction(state, category, detectionId),
  );
  const setLastAction = useDetectedEntitiesStore(state => state.setLastAction);
  const selectUpsertPayload = useDetectedEntitiesStore(state =>
    getPayloadForNewDetectionSelector(state),
  );
  const selectUpsertPayloadForUpdate = useDetectedEntitiesStore(state =>
    getPayloadForUpdateDetectionSelector(state),
  );
  const existingFields = useDetectedEntitiesStore(state =>
    selectExistingFields(state, category, detectionId),
  );
  const existingOwners = useDetectedEntitiesStore(state =>
    selectExistingOwners(state, category, detectionId),
  );
  const setResolved = useDetectedEntitiesStore(state => state.setResolved);
  const setActioning = useDetectedEntitiesStore(state => state.setActioning);

  const upsertDetection = async (clientId: string, detectionId: string) => {
    const upsertPayloadForUpdate = selectUpsertPayloadForUpdate(category, detectionId, fieldName);
    if (!upsertPayloadForUpdate) {
      throw new Error('Failed upserting item');
    }

    setActioning(true, category, detectionId, fieldName);
    try {
      await upsertEntity(clientId, upsertPayloadForUpdate, false, entityFieldsToFilterOut);
      setResolved(true, category, detectionId, fieldName);
      setLastAction(
        category,
        detectionId,
        {
          type: 'update',
          targetEntityFieldsMetadata: upsertPayloadForUpdate.entityFieldsMetadata,
          targetEntityType: upsertPayloadForUpdate.entityType,
          previousValues: getPreviousValues(upsertPayloadForUpdate.values as UIEntityFields),
        },
        fieldName,
      );
    } catch (e) {
      setResolved(false, category, detectionId, fieldName);
      if (lastAction != null) {
        setLastAction(category, detectionId, lastAction, fieldName);
      }
      throw e;
    } finally {
      setActioning(false, category, detectionId, fieldName);
    }
  };

  // `FieldValue` has no array arm; owner lists ride through the payload types as one, the same
  // way the store's own owners fragment does.
  const noOwners = [] as unknown as FieldValue;

  // Owners are not a detected field, so they are absent from `existingFields`. An update that
  // rewrites them must carry the record's own owners into the snapshot — an empty list where it
  // had none, which undoes back to unowned rather than leaving the adviser's set in place.
  const previousOwners = (values: UIEntityFields): EntityFields =>
    'owners' in values ? { owners: existingOwners ?? noOwners } : {};

  const getPreviousValues = (values: UIEntityFields) => {
    const id = values['id']?.toString();
    if (fieldName) {
      const previousField = existingFields.find(f => f.key === fieldName);
      return { [fieldName]: previousField?.value || null, ...previousOwners(values) };
    }

    return existingFields
      .filter(({ key }) => !fieldName || key === fieldName)
      .reduce(
        (acc, currentValue) => {
          return { ...acc, ...{ [currentValue.key]: currentValue.value } };
        },
        { ...(id ? { id } : {}), ...previousOwners(values) },
      );
  };

  const insertDetectionEntity = async (
    clientId: string,
    category: EntityCategory,
    detectionId: string,
  ) => {
    const upsertPayload = selectUpsertPayload(category, detectionId, fieldName);
    if (!upsertPayload) {
      return;
    }

    setActioning(true, category, detectionId, fieldName);
    try {
      const upsertedId = await upsertEntity(
        clientId,
        upsertPayload,
        false,
        entityFieldsToFilterOut,
      );

      if (!upsertedId) {
        Logger.error(
          'BUG! Inserted new detection entity but no ID was returned',
          {},
          { detectionId, clientId },
        );
        return;
      }

      setResolved(true, category, detectionId);
      setLastAction(category, detectionId, {
        type: 'insert',
        targetEntityFieldsMetadata: upsertPayload.entityFieldsMetadata,
        targetEntityType: upsertPayload.entityType,
        insertedEntityId: upsertedId,
      });
    } catch (e) {
      setResolved(false, category, detectionId, fieldName);
      throw e;
    } finally {
      setActioning(false, category, detectionId, fieldName);
    }
  };

  return {
    upsertDetection,
    insertDetectionEntity,
  };
}

type UndoOperation =
  | { type: 'skip' }
  | { type: 'storeOnly'; applyStoreUpdate: () => void }
  | { type: 'delete'; entityType: string; entityId: string; applyStoreUpdate: () => void }
  | { type: 'upsert'; payload: UpsertEntityInput; applyStoreUpdate: () => void };

function useUndoOperationBuilder() {
  const selectDetection = useDetectedEntitiesStore(selectDetectionSelector);
  const setDiscarded = useDetectedEntitiesStore(state => state.setDiscarded);
  const setResolved = useDetectedEntitiesStore(state => state.setResolved);
  const setLastAction = useDetectedEntitiesStore(state => state.setLastAction);
  const setExistingValues = useDetectedEntitiesStore(state => state.setExistingValues);

  function buildOperation(
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName: string | undefined,
    warnWhenNoLastAction: boolean,
    scopedFieldNames?: string[],
  ): UndoOperation {
    const detectionState = selectDetection(category, detectionId);

    if (
      detectionState == null ||
      detectionState.isDiscarded ||
      (fieldName && detectionState.fieldsByKey[fieldName]?.isDiscarded)
    ) {
      return {
        type: 'storeOnly',
        applyStoreUpdate: () =>
          setDiscarded(false, category, detectionId, {
            fieldNames: fieldName ? [fieldName] : undefined,
          }),
      };
    }

    const lastAction = detectionState.lastAction;
    if (lastAction === null) {
      if (warnWhenNoLastAction) {
        notifyAndWarn(
          'Nothing to undo',
          'No action found to undo. Contact support if the issue persists.',
          {
            detectionId,
            fieldName,
          },
        );
      }
      return { type: 'skip' };
    }

    if (lastAction.type === 'insert') {
      return {
        type: 'delete',
        entityType: lastAction.targetEntityType,
        entityId: lastAction.insertedEntityId,
        applyStoreUpdate: () => {
          setResolved(false, category, detectionId);
          setLastAction(category, detectionId, null, fieldName);
        },
      };
    }

    const { targetEntityType, targetEntityFieldsMetadata, previousValues } = lastAction;

    if (fieldName && !(fieldName in previousValues)) {
      notifyAndWarn(
        'Nothing to undo',
        'No action found to undo. Contact support if the issue persists.',
        {
          detectionId,
          fieldName,
        },
      );
      return { type: 'skip' };
    }

    const fields = scopedFieldNames ?? (fieldName ? [fieldName] : null);
    // An owners rewrite belongs to the whole record, never to one field, so a scoped undo still
    // has to restore it alongside the field it names.
    const ownersToRestore: EntityFields =
      'owners' in previousValues ? { owners: previousValues.owners } : {};
    const values: EntityFields = fields
      ? fields.reduce<EntityFields>((acc, f) => {
          if (f in previousValues) acc[f] = previousValues[f];
          return acc;
        }, ownersToRestore)
      : previousValues;

    return {
      type: 'upsert',
      payload: {
        entityType: targetEntityType,
        entityFieldsMetadata: targetEntityFieldsMetadata,
        values,
      },
      applyStoreUpdate: () => {
        setExistingValues(category, detectionId, values);
        if (fields) {
          fields.forEach(f => setLastAction(category, detectionId, null, f));
          fields.forEach(f => setResolved(false, category, detectionId, f));
        } else {
          setLastAction(category, detectionId, null, fieldName);
          setResolved(false, category, detectionId, fieldName);
        }
      },
    };
  }

  return { buildOperation, selectDetection, setDiscarded };
}

export function useUndoMutation() {
  const { flagEnabledBatchCrudDataUpload } = useFlags();
  const { invalidateDetections } = useInvalidateDetections();
  const selectDetection = useDetectedEntitiesStore(selectDetectionSelector);
  const { deleteEntityByTypeAndId } = useDeleteEntities();
  const setResolved = useDetectedEntitiesStore(state => state.setResolved);
  const { upsertEntity } = useUpsertEntity();
  const setLastAction = useDetectedEntitiesStore(state => state.setLastAction);
  const setExistingValues = useDetectedEntitiesStore(state => state.setExistingValues);
  const setDiscarded = useDetectedEntitiesStore(state => state.setDiscarded);
  const setActioning = useDetectedEntitiesStore(state => state.setActioning);

  const undoDetectionLastAction = async (
    clientId: string,
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName?: string,
    warnWhenNoLastAction: boolean = true,
  ) => {
    const detectionState = selectDetection(category, detectionId);

    Logger.info('undoDetectionLastAction started', {
      category,
      clientId,
      detectionId,
      fieldName,
      currentLastAction: detectionState?.lastAction ?? null,
    });

    if (
      detectionState == null ||
      detectionState?.isDiscarded ||
      (fieldName && detectionState?.fieldsByKey[fieldName]?.isDiscarded)
    ) {
      setDiscarded(false, category, detectionId, {
        fieldNames: fieldName ? [fieldName] : undefined,
      });
      return;
    }

    const lastAction = detectionState.lastAction;
    if (lastAction === null) {
      if (warnWhenNoLastAction) {
        notifyAndWarn(
          'Nothing to undo',
          'No action found to undo. Contact support if the issue persists.',
          {
            detectionId,
            clientId,
            fieldName,
          },
        );
      }
      return;
    }

    setActioning(true, category, detectionId, fieldName);
    try {
      if (lastAction.type === 'insert') {
        const { targetEntityType, insertedEntityId } = lastAction;
        await deleteEntityByTypeAndId(clientId, targetEntityType, insertedEntityId);
        setResolved(false, category, detectionId);
        Logger.info('undoDetectionLastAction calling setLastAction (insert)', {
          detectionId,
          fieldName,
          lastActionType: 'insert',
        });
        setLastAction(category, detectionId, null, fieldName);
      }

      if (lastAction.type === 'update') {
        const { targetEntityType, targetEntityFieldsMetadata, previousValues } = lastAction;
        if (fieldName && !(fieldName in previousValues)) {
          notifyAndWarn(
            'Nothing to undo',
            'No action found to undo. Contact support if the issue persists.',
            {
              detectionId,
              clientId,
              fieldName,
            },
          );
          return;
        }

        const values = fieldName
          ? {
              [fieldName]: previousValues[fieldName],
              ...('owners' in previousValues ? { owners: previousValues.owners } : {}),
            }
          : previousValues;

        const upsertPayload = {
          entityType: targetEntityType,
          entityFieldsMetadata: targetEntityFieldsMetadata,
          values,
        };
        await upsertEntity(clientId, upsertPayload, true, entityFieldsToFilterOut);
        setExistingValues(category, detectionId, values);
        Logger.info('undoDetectionLastAction calling setLastAction (update)', {
          detectionId,
          fieldName,
          lastActionType: 'update',
        });
        setLastAction(category, detectionId, null, fieldName);
        setResolved(false, category, detectionId, fieldName);
      }
    } catch (e) {
      notifyAndLogError(
        'Failed to undo',
        'We were unable to undo this action. Please try again or contact support.',
        e,
        { detectionId, clientId, fieldName },
      );
      if (!flagEnabledBatchCrudDataUpload) {
        await invalidateDetections();
      }
    } finally {
      setActioning(false, category, detectionId, fieldName);
    }
  };

  return {
    undoDetectionLastAction,
  };
}

export function useUndoAllMutation() {
  const { flagEnabledBatchCrudDataUpload } = useFlags();
  const selectDetectionsByCategory = useDetectedEntitiesStore(getDetectionsByCategorySelector);
  const { buildOperation, setDiscarded } = useUndoOperationBuilder();
  const { undoDetectionLastAction } = useUndoMutation();
  const { deleteEntitiesBatch } = useDeleteEntities();
  const { upsertEntities } = useUpsertEntity();
  const queryClient = useQueryClient();
  const setCategoryActioning = useDetectedEntitiesStore(state => state.setCategoryActioning);
  const setEntitiesOfTypeActioning = useDetectedEntitiesStore(
    state => state.setEntitiesOfTypeActioning,
  );

  async function undoAllInCategory(clientId: string, category: EntityCategory) {
    setCategoryActioning(true, category);
    try {
      await undoAllDetections(clientId, category, selectDetectionsByCategory(category), false);
    } finally {
      setCategoryActioning(false, category);
    }
  }

  async function undoAllInCategoryAndEntityType(
    clientId: string,
    category: EntityCategory,
    entityType: string,
    warnWhenNoLastAction: boolean,
  ) {
    setEntitiesOfTypeActioning(true, category, entityType);
    try {
      await undoAllDetections(
        clientId,
        category,
        selectDetectionsByCategory(category, entityType),
        warnWhenNoLastAction,
      );
    } finally {
      setEntitiesOfTypeActioning(false, category, entityType);
    }
  }

  async function undoAllDetections(
    clientId: string,
    category: EntityCategory,
    detections: DetectionState[],
    warnWhenNoLastAction: boolean,
  ) {
    Logger.info('undoAllDetections started', {
      category,
      clientId,
      numDetections: detections.length,
      detectionIds: detections.map(d => d.id),
      detectionsWithMultipleFields: detections
        .filter(d => !d.isConflictable)
        .map(d => ({
          id: d.id,
          numFields: Object.keys(d.fieldsByKey).length,
          lastActionType: d.lastAction?.type ?? null,
        })),
    });

    if (!flagEnabledBatchCrudDataUpload) {
      await Promise.all(
        detections.flatMap(detection => {
          if (detection.isConflictable) {
            return undoDetectionLastAction(
              clientId,
              category,
              detection.id,
              undefined,
              warnWhenNoLastAction,
            );
          }

          const discardedFields = Object.values(detection.fieldsByKey)
            .filter(field => field.isDiscarded)
            .map(field => field.key);

          if (detection.isDiscarded) {
            setDiscarded(false, category, detection.id);
          } else if (discardedFields.length > 0) {
            setDiscarded(false, category, detection.id, { fieldNames: discardedFields });
          }

          const fields = Object.keys(detection.fieldsByKey)
            .filter(f => !fieldsToHide.includes(f) && detection.fieldsByKey[f].value !== null)
            .filter(f => !discardedFields.includes(f));

          return fields.map(field =>
            undoDetectionLastAction(clientId, category, detection.id, field, warnWhenNoLastAction),
          );
        }),
      );
      return;
    }

    const toDelete: { entityType: string; entityId: string }[] = [];
    const toUpsert: UpsertEntityInput[] = [];
    const storeUpdates: (() => void)[] = [];

    for (const detection of detections) {
      if (detection.isConflictable) {
        const op = buildOperation(category, detection.id, undefined, warnWhenNoLastAction);
        if (op.type === 'skip') continue;
        if (op.type === 'storeOnly') {
          storeUpdates.push(op.applyStoreUpdate);
          continue;
        }
        if (op.type === 'delete')
          toDelete.push({ entityType: op.entityType, entityId: op.entityId });
        if (op.type === 'upsert') toUpsert.push(op.payload);
        storeUpdates.push(op.applyStoreUpdate);
      } else {
        const discardedFields = Object.values(detection.fieldsByKey)
          .filter(field => field.isDiscarded)
          .map(field => field.key);

        if (detection.isDiscarded) {
          storeUpdates.push(() => setDiscarded(false, category, detection.id));
          continue;
        }

        if (discardedFields.length > 0) {
          storeUpdates.push(() =>
            setDiscarded(false, category, detection.id, { fieldNames: discardedFields }),
          );
        }

        const activeFields = Object.keys(detection.fieldsByKey)
          .filter(f => !fieldsToHide.includes(f) && detection.fieldsByKey[f].value !== null)
          .filter(f => !discardedFields.includes(f));

        if (activeFields.length > 0) {
          // scopedFieldNames scopes the upsert values to the active fields only
          const op = buildOperation(
            category,
            detection.id,
            undefined,
            warnWhenNoLastAction,
            activeFields,
          );
          if (op.type === 'upsert') {
            toUpsert.push(op.payload);
            storeUpdates.push(op.applyStoreUpdate);
          }
        } else if (warnWhenNoLastAction && detection.lastAction === null) {
          notifyAndWarn(
            'Nothing to undo',
            'No action found to undo. Contact support if the issue persists.',
            {
              detectionId: detection.id,
              clientId,
            },
          );
        }
      }
    }

    try {
      await Promise.all([
        deleteEntitiesBatch(clientId, toDelete),
        toUpsert.length > 0
          ? upsertEntities(clientId, toUpsert, true, entityFieldsToFilterOut, false)
          : Promise.resolve(),
      ]);
      storeUpdates.forEach(update => update());
    } catch (e) {
      notifyAndLogError(
        'Failed to undo',
        'We were unable to undo all actions. Please try again or contact support.',
        e,
        { clientId, category },
      );
      // Force a full refresh including detections to ensure the UI reflects the actual server state,
      // since one or both batch operations may have partially succeeded.
      queryClient.invalidateQueries({ queryKey: ['clients', clientId] });
    }
  }

  return { undoAllInCategory, undoAllInCategoryAndEntityType };
}
